<template>
  <div class="popover-wrapper">
    <Popover
      ref="popoverRef"
      position="top-center"
      color="unset"
      :popover-style="popoverStyle"
      :with-overlay="true"
    >
      <template #content="{ on }">
        <UIInput
          :id="inputId"
          :value="displayedValue"
          :type="isPrice ? 'text' : 'number'"
          :style="{
            borderColor:
              isPrice && !isValidInternal && internalValue !== previousValue
                ? 'red'
                : '',
          }"
          :disabled="isDisabled"
          readonly
          @click="inputClicked"
          v-on="on"
        />
      </template>

      <template #popoverContent>
        <div class="popover-wrapper__content">
          <label>{{ text }}</label>
          <p v-if="description" class="description-text">{{ description }}</p>
          <UIInput
            v-if="!isPrice"
            :id="inputId"
            ref="popoverInput"
            type="number"
            :value="internalValue"
            :style="{
              borderColor:
                isPrice && !isValidInternal && internalValue !== previousValue
                  ? 'red'
                  : '',
            }"
            @input="validateInput"
            @blur="handleBlur"
            @vue:mounted="focusPopoverInput"
          />
          <CurrencyInput
            v-else
            :id="inputId"
            ref="popoverInput"
            v-model="internalValue"
            color="unset"
            :input="handleInputChange"
            :blur="handleBlurChange"
            :options="{
              locale: 'tr-TR',
              currency: 'TRY',
              currencyDisplay: 'hidden',
              hideGroupingSeparatorOnFocus: false,
              hideNegligibleDecimalDigitsOnFocus: true,
              autoDecimalDigits: false,
              useGrouping: true,
              accountingSign: false,
              valueRange: { min: 0 },
              precision: { min: 0, max: 2 },
            }"
            :style="{
              borderColor:
                isPrice && !isValidInternal && internalValue !== previousValue
                  ? 'red'
                  : '',
              border:
                isPrice && !isValidInternal && internalValue !== previousValue
                  ? '1px solid red'
                  : '',
            }"
            @focus="$event.target.select()"
          />
          <p
            v-if="
              isPrice &&
              !isValidInternal &&
              internalValue !== previousValue &&
              currentErrorMessage
            "
            class="error-text"
          >
            {{ currentErrorMessage }}
          </p>
          <UIButton
            primary
            sm
            :disabled="(isPrice && !isValidInternal) || !internalValue"
            :class="{
              'disabled-button':
                (isPrice && !isValidInternal) || !internalValue,
            }"
            @click="handleUpdate"
          >
            Kaydet
          </UIButton>
        </div>
      </template>
    </Popover>
  </div>
</template>

<script>
import {
  Popover,
  Input as UIInput,
  Button as UIButton,
} from '@n11/so-ui-components';
import CurrencyInput from './CurrencyInput';
import { useStore } from 'vuex';
import { watch } from 'vue';

export default {
  name: 'PopoverInput',
  components: { Popover, UIInput, UIButton, CurrencyInput },
  props: {
    inputId: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Number],
      default: undefined,
    },
    text: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: false,
      default: '',
    },
    validate: {
      type: Function,
      required: true,
    },
    isValid: {
      type: Boolean,
      required: true,
    },
    isPrice: {
      type: Boolean,
      required: false,
      default: false,
    },
    format: {
      type: Function,
      required: false,
      default: (value) => value,
    },
    isDisabled: {
      type: Boolean,
      default: false,
      required: false,
    },
    isActionClick: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      required: false,
      default: undefined,
    },
    errorMessage: {
      type: String,
      required: false,
      default: '',
    },
    getErrorMessageFn: {
      type: Function,
      required: false,
      default: null,
    },
    popoverStyle: {
      type: Object,
      required: false,
      default: () => ({
        padding: '0px',
        top: '100px',
      }),
    },
  },
  emits: ['input', 'validity', 'update'],
  data() {
    const initial = this.normalizeIncomingValue(this.value);
    return {
      internalValue: initial,
      isValidInternal: this.isValid,
      popoverRef: null,
      previousValue: initial,
      timeout: null,
    };
  },
  computed: {
    displayedValue() {
      return this.format(this.internalValue);
    },
    currentErrorMessage() {
      if (
        this.getErrorMessageFn &&
        typeof this.getErrorMessageFn === 'function'
      ) {
        return this.getErrorMessageFn(this.internalValue);
      }
      return this.errorMessage;
    },
  },
  watch: {
    isValid(newValidity) {
      this.isValidInternal = newValidity;
    },
    value(newValue) {
      const next = this.normalizeIncomingValue(newValue);
      this.internalValue = next;
      this.previousValue = next;
    },
  },
  mounted() {
    const store = useStore();
    watch(
      () => store.getters['products/getIsProductUpdate'],
      (newValue) => {
        if (
          newValue &&
          store.getters['products/getActivePriceAreaInput'] === this.inputId
        ) {
          this.$refs.popoverRef?.popoverToogle();
        }
      },
      { immediate: true },
    );
  },
  methods: {
    normalizeIncomingValue(val) {
      if (!this.isPrice) return val;
      if (val === null || val === undefined || val === '') return undefined;
      return val;
    },
    handleInputChange() {
      if (this.timeout) clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.isValidInternal = this.validate(this.internalValue);
        this.$emit('input', this.internalValue);
        this.$emit('validity', this.isValidInternal);
      }, 50);
    },

    validateInput(e) {
      const value = e.target.value;
      if (!this.isPrice && !/^\d+$/.test(value)) return;

      this.internalValue = value;
      if (this.timeout) clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.isValidInternal = this.validate(this.internalValue);
        this.$emit('input', this.internalValue);
        this.$emit('validity', this.isValidInternal);
      }, 50);
    },

    handleUpdate() {
      if (!this.isPrice && !/^\d+$/.test(this.internalValue)) {
        return;
      }

      const revert = () => {
        this.internalValue = this.previousValue;
        this.$refs.popoverInput?.setValue(this.previousValue);

        return false;
      };

      if (this.isPrice && !this.isValidInternal) {
        return revert();
      }

      if (this.timeout) clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        if (this.validate(this.internalValue)) {
          this.$emit('update', this.internalValue, revert);
        }
      }, 50);
    },

    handleBlur(e) {
      const value = e.target.value;
      if (!this.isPrice && !/^\d+$/.test(value)) {
        this.internalValue = this.previousValue;
        this.$refs.popoverInput?.setValue(this.previousValue);
        return;
      }

      if (this.timeout) clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        if (this.isPrice && this.internalValue !== this.previousValue) {
          this.internalValue = this.previousValue;
          this.$refs.popoverInput?.setValue(this.internalValue);
        }
      }, 150);
    },

    handleBlurChange() {
      if (!this.internalValue && this.previousValue) {
        if (this.timeout) clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          this.internalValue = this.previousValue;
          this.$refs.popoverInput?.setValue(this.previousValue);
        }, 150);
      }
    },

    inputClicked(e) {
      if (this.isPrice) {
        e.target.select();
      }
    },

    focusPopoverInput() {
      this.$nextTick(() => {
        this.$refs.popoverInput?.$el?.querySelector('input')?.focus();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables';

.popover-wrapper {
  :deep(.popover-text) {
    z-index: 9999 !important;
    margin-top: 100px !important;
  }

  &__content {
    display: inline-flex;
    width: 250px;
    flex-direction: column;
    align-items: flex-start;
    padding: 12px;
    border: 1px solid $colorN50;
    border-radius: 8px;
    background: $colorN00;
    box-shadow: 0 0 0 3px $gray-32;
    gap: 10px;

    label {
      color: $colorN80;
      font-size: 12px;
      font-weight: 700;
      line-height: 16px;
    }

    .description-text {
      margin: 0;
      margin-top: -4px;
      color: $colorN60;
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      text-align: left;
    }

    .error-text {
      margin: 0;
      margin-top: -4px;
      color: $colorE50;
      font-size: 11px;
      font-weight: 400;
      line-height: 14px;
      text-align: left;
    }
  }

  .disabled-button {
    background-color: grey;
    cursor: not-allowed;
  }
}
</style>
